import React, {
  forwardRef,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { Path, useLocation } from "react-router";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { AnyPeriod, classNames, GQLPeriod, useIsMounted } from "@ct-react/core";
import { supportedLocales, useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { AccommodationType } from "@shared/models/article";
import { useDisplayUntilDesktop } from "../../../utils/breakpoints";
import {
  bookingTranslations,
  buttonsTranslations,
  genericTermTranslations,
  globalTranslations
} from "../../../i18n/sharable-defs";
import { searchbar } from "../translations";
import { useSearchContext } from "../../../contexts/search";
import { useScrollLock } from "../../../contexts/lockScroll";
import { countGuests, GuestsInput } from "../../../models/search";
import SearchBarItem from "./search-bar-item";
import SearchItemSelectorDisplayType from "./item-selectors/display-type";
import SearchItemSelectorPeriod from "./item-selectors/period";
import SearchItemSelectorObjectType from "./item-selectors/object-type";
import SearchItemSelectorGuests from "./item-selectors/guests";
import SearchItemSelectorRange from "./item-selectors/range";
import ArrowRightSvg from "../../../../assets/svg/custom_arrow_down.svg";
import "./search-bar.scss";

type Position = "down" | "up";

type FiltersProps = {
  displayType: AccommodationType;
  applyChanges?: (type: AccommodationType) => void;
  isMobile?: boolean;
};

type FiltersHandle = {
  applySearch: (type: AccommodationType) => void;
  clear: (type: AccommodationType) => void;
  resume: (type: AccommodationType) => string[];
};

const getBodyScrolled = (scroll: number) => scroll >= 200;
const getMobileVisible = (path: Path): boolean => !/\/checkout/.test(path.pathname) && !/refId=/.test(path.search);
const getPosition = (path: Path): Position => new RegExp(`^\/(${supportedLocales.map(l => l.basename).join("|")})\/$`).test(path.pathname) ? "down" : "up";
const getDisplayType = (path: Path): AccommodationType | undefined => {
  switch (true) {
    case /^\/(.*)\/location\/vacances/.test(path.pathname): return AccommodationType.RENTAL;
    case /^\/(.*)\/location\/saison/.test(path.pathname): return AccommodationType.SEASONAL;
    case /^\/(.*)\/location\/annee/.test(path.pathname): return AccommodationType.ANNUAL;
    case /^\/(.*)\/vente/.test(path.pathname): return AccommodationType.SALE;
    default: return undefined;
  }
}

const SearchFilters = forwardRef<FiltersHandle, FiltersProps>((
  {
    displayType,
    isMobile = false
  }: FiltersProps, forwardedRef) => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const navigate = useLocaleNavigate();
  const { print } = useLocaleFormatter();
  const [ searchCriteria, changeCriteria ] = useSearchContext();

  const [ objectTypes, setObjectTypes ] = useState<number[] | undefined>(searchCriteria[displayType].objectTypes);
  const [ surface, setSurface ] = useState<number | undefined>(searchCriteria[displayType].surface);
  const [ roomsRange, setRoomsRange ] = useState<[ number, number ] | undefined>(searchCriteria[displayType].roomsRange);
  const [ priceRange, setPriceRange ] = useState<[ number, number ] | undefined>(searchCriteria[displayType].priceRange);
  const [ guests, setGuests ] = useState<GuestsInput | undefined>(searchCriteria[displayType].guests);
  const [ period, setPeriod ] = useState<{ period: AnyPeriod } | undefined>(searchCriteria[displayType].period);

  const priceConfig = useMemo(() => ({
    max: 20000,
    step: 500,
    ...(displayType === AccommodationType.SALE) && {
      max: 3000000,
      step: 100000
    }
  }), [ displayType ]);


  // Effects

  useImperativeHandle(forwardedRef, () => ({
    applySearch: (type: AccommodationType) => {
      switch (type) {
        case AccommodationType.RENTAL:
          changeCriteria({
            type: AccommodationType.RENTAL,
            payload: { objectTypes, period, roomsRange, guests }
          });
          navigate("/location/vacances/");
          break;
        case AccommodationType.SEASONAL:
          changeCriteria({
            type: AccommodationType.SEASONAL,
            payload: { objectTypes, priceRange, roomsRange }
          });
          navigate("/location/saison/");
          break;
        case AccommodationType.ANNUAL:
          changeCriteria({
            type: AccommodationType.ANNUAL,
            payload: { objectTypes, priceRange, roomsRange, surface }
          });
          navigate("/location/annee/");
          break;
        case AccommodationType.SALE:
          changeCriteria({
            type: AccommodationType.SALE,
            payload: { objectTypes, priceRange, roomsRange, surface }
          });
          navigate("/vente/");
          break;
      }
    },
    clear: (type: AccommodationType) => {
      setObjectTypes(undefined);
      setRoomsRange(undefined);
      type !== AccommodationType.RENTAL && setPriceRange(undefined);
      type === AccommodationType.RENTAL && setPeriod(undefined);
      type === AccommodationType.RENTAL && setGuests(undefined);
      [ AccommodationType.SEASONAL, AccommodationType.SALE ].includes(type) && setSurface(undefined);
    },
    resume: (type: AccommodationType) => {
      const context = searchCriteria[type];
      const objectTypesLabel = intl.formatMessage(searchbar.objectTypesFullResume, { count: context.objectTypes?.length || 0 });
      const priceLabel = !!context.priceRange
        ? print.priceRange(context.priceRange.map((p: number) => `${p} CHF`), { trailingZeroDisplay: "stripIfInteger"}, true)
        : intl.formatMessage(searchbar.priceFullResume);
      switch (type) {
        case AccommodationType.RENTAL:
          const periodLabel = !!context.period
            ? print.period(context.period.period, true)
            : intl.formatMessage(searchbar.periodFullResume);
          return [ objectTypesLabel, periodLabel ];
        case AccommodationType.SEASONAL:
        case AccommodationType.ANNUAL:
        case AccommodationType.SALE:
          return [ objectTypesLabel, priceLabel ];
      }
    }
  }));

  // update filters state of display type changes
  useEffect(() => {
    if (!isMounted) return;
    setObjectTypes(searchCriteria[displayType].objectTypes);
    setSurface(searchCriteria[displayType].surface);
    setRoomsRange(searchCriteria[displayType].roomsRange);
    setPriceRange(searchCriteria[displayType].priceRange);
    setGuests(searchCriteria[displayType].guests);
    setPeriod(searchCriteria[displayType].period);
  }, [ displayType ]);


  // rendering

  const formatRoom = useCallback((value: number): string => {
    switch(true) {
      case value <= 1: return "Studio";
      case value >= 6: return "6+";
      default: return value.toString();
    }
  }, []);

  return (
    <>
      <SearchBarItem className="object-types"
                     value={objectTypes}
                     title={intl.formatMessage(searchbar.objectTypesTitle)}
                     resume={types => intl.formatMessage(searchbar.objectTypesResume, { count: types.length })}
                     fallback={intl.formatMessage(searchbar.objectTypesResume, { count: 0 })}
                     onReset={() => setObjectTypes(undefined)}>
        <SearchItemSelectorObjectType type={displayType}
                                      value={objectTypes}
                                      onChange={setObjectTypes} />
      </SearchBarItem>

      {[ AccommodationType.SEASONAL, AccommodationType.ANNUAL, AccommodationType.SALE ].includes(displayType) &&
        <SearchBarItem className="price"
                       value={priceRange}
                       title={intl.formatMessage(searchbar.priceTitle)}
                       resume={range => print.priceRange(range.map((p: number) => `${p} CHF`), { trailingZeroDisplay: "stripIfInteger"})}
                       fallback={intl.formatMessage(bookingTranslations.price)}
                       onReset={() => setPriceRange(undefined)}>
          <SearchItemSelectorRange minMax={true}
                                   limit={[0, priceConfig.max]}
                                   step={priceConfig.step}
                                   resume={value => print.price(`${value} CHF`, { trailingZeroDisplay: "stripIfInteger" })}
                                   value={priceRange}
                                   onChange={setPriceRange} />
        </SearchBarItem>
      }

      {[ AccommodationType.RENTAL ].includes(displayType) &&
        <SearchBarItem className="period"
                       value={period}
                       title={intl.formatMessage(bookingTranslations.choose_date)}
                       resume={({ period }) => print.period(period as GQLPeriod, true)}
                       fallback={intl.formatMessage(bookingTranslations.checkIn_checkOut)}
                       onReset={() => setPeriod(undefined)}>
          <SearchItemSelectorPeriod mobile={isMobile}
                                    value={period}
                                    onChange={setPeriod} />
        </SearchBarItem>
      }

      <SearchBarItem className="rooms"
                     value={roomsRange}
                     title={intl.formatMessage(searchbar.roomsTitle)}
                     resume={([ min, max ]) => `${formatRoom(min)} - ${formatRoom(max)}`}
                     fallback={intl.formatMessage(bookingTranslations.rooms)}
                     onReset={() => setRoomsRange(undefined)}>
        <SearchItemSelectorRange minMax={true}
                                 limit={[1, 6]}
                                 resume={val => formatRoom(val)}
                                 value={roomsRange}
                                 onChange={setRoomsRange} />
      </SearchBarItem>

      {[ AccommodationType.ANNUAL, AccommodationType.SALE ].includes(displayType) &&
        <SearchBarItem value={surface}
                       title={intl.formatMessage(searchbar.surfaceTitle)}
                       resume={value => `${value} m²`}
                       fallback={intl.formatMessage(bookingTranslations.livingSpace)}
                       onReset={() => setSurface(undefined)}>
          <SearchItemSelectorRange limit={[0, 250]}
                                   step={10}
                                   resume={value => `${value} m²`}
                                   value={surface}
                                   onChange={setSurface} />
        </SearchBarItem>
      }

      {[ AccommodationType.RENTAL ].includes(displayType) &&
        <SearchBarItem className="guests" value={countGuests(guests)}
                       title={intl.formatMessage(searchbar.guestsTitle)}
                       resume={total => intl.formatMessage(bookingTranslations.nbrOfGuests, { total })}
                       fallback={intl.formatMessage(bookingTranslations.nbrOfGuests, { total: 0 })}
                       onReset={() => setGuests(undefined)}>
          <SearchItemSelectorGuests value={guests} onChange={setGuests} />
        </SearchBarItem>
      }
  </>);

});

const SearchBar = () => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const location = useLocation();
  const { lockScroll, unlockScroll } = useScrollLock();

  const [ bodyScrolled, setBodyScrolled ] = useState<boolean>(getBodyScrolled(window.scrollY));
  const [ mobileVisible, setMobileVisible ] = useState<boolean>(getMobileVisible(location));
  const [ mobileOpen, setMobileOpen ] = useState<boolean>(false);
  const [ position, setPosition ] = useState<Position>(getPosition(location));
  const [ displayType, setDisplayType ] = useState<AccommodationType>(getDisplayType(location) || AccommodationType.RENTAL);
  const filtersRef = useRef<FiltersHandle>(null);


  // effects

  // listen for scrolling to define position
  useEffect(() => {
    const onScroll = () => {
      const newValue = getBodyScrolled(window.scrollY);
      (newValue !== bodyScrolled) && setBodyScrolled(newValue);
    }
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll)
  }, [ bodyScrolled ]);

  // redefine search position and display regarding url changes
  useEffect(() => {
    if (!isMounted) return;
    setMobileVisible(getMobileVisible(location));
    setPosition(getPosition(location));
    setDisplayType(previous => getDisplayType(location) || previous);
  }, [ location ]);

  // lock screen on mobile
  useEffect(() => {
    if (mobileOpen) lockScroll();
    else unlockScroll();
  }, [ mobileOpen ]);


  // dom interactions

  const onSearch = (e: SyntheticEvent<HTMLFormElement | HTMLButtonElement>) => {
    e.preventDefault();
    filtersRef.current?.applySearch(displayType);
  }


  // rendering

  const isMobile = useDisplayUntilDesktop();
  const wrapperClasses = classNames("search-bar", {
    mobile: isMobile,
    desktop: !isMobile,
    down: position === "down" && !bodyScrolled,
    hide: isMobile && !mobileVisible
  });

  const formatType = useCallback((value: AccommodationType) => {
    switch (value) {
      case AccommodationType.RENTAL: return intl.formatMessage(genericTermTranslations.locationHolidays);
      case AccommodationType.SEASONAL: return intl.formatMessage(genericTermTranslations.locationSeasonal);
      case AccommodationType.ANNUAL: return intl.formatMessage(genericTermTranslations.locationYearly);
      case AccommodationType.SALE: return intl.formatMessage(genericTermTranslations.sale)
    }
  }, [ intl ]);

  const filtersRendering = useMemo(() => (
    <SearchFilters ref={filtersRef} displayType={displayType} isMobile={isMobile} />), [ displayType, isMobile ]);


  if (isMobile) {
    return (
      <>

        <div className={wrapperClasses}>

          <SearchBarItem className={classNames("display-type", { active: displayType !== AccommodationType.SALE })}
                         value={displayType !== AccommodationType.SALE ? displayType : null}
                         title={intl.formatMessage(searchbar.typeTitle)}
                         resume={value => formatType(value)}
                         fallback={intl.formatMessage(genericTermTranslations.rent)}
                         closeOnSelect={true}
                         withActions={false}>
            <SearchItemSelectorDisplayType value={displayType}
                                           onChange={value => setDisplayType(value || AccommodationType.RENTAL)} />
          </SearchBarItem>

          <button type="button"
                  className={classNames("search-bar-item", "no-border", { active: displayType === AccommodationType.SALE })}
                  onClick={() => setDisplayType(AccommodationType.SALE)}>
            {intl.formatMessage(genericTermTranslations.sale)}
          </button>

          <div className="full-resume" onClick={() => setMobileOpen(true)}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span className="textual">{filtersRef.current?.resume(displayType).join(" • ")}</span>
          </div>
        </div>

        <div className={classNames("mobile-full-search", { show: mobileOpen && isMobile })}>
          <div className="header">
            <h3>{formatType(displayType)}</h3>
            <FontAwesomeIcon icon={faXmark} onClick={() => setMobileOpen(false)} />
          </div>
          <div className="scroll">
            <div className="search-items">{filtersRendering}</div>
          </div>
          <div className="footer">
            <button type="button" className="btn"
                    onClick={() => filtersRef.current?.clear(displayType)}>
              {intl.formatMessage(buttonsTranslations.reset_all)}
            </button>
            <button type="button" className="btn-red"
                    onClick={(e) => { setMobileOpen(false); onSearch(e); }}>
              {intl.formatMessage(globalTranslations.search)}
            </button>
          </div>
        </div>

      </>);
  }

  return (
    <form className={wrapperClasses}
          onSubmit={onSearch}>

      <SearchBarItem className={classNames("display-type", { active: displayType !== AccommodationType.SALE })}
                     value={displayType !== AccommodationType.SALE ? displayType : null}
                     title={intl.formatMessage(searchbar.typeTitle)}
                     resume={value => formatType(value)}
                     fallback={intl.formatMessage(genericTermTranslations.rent)}
                     closeOnSelect={true}
                     withActions={false}>
        <SearchItemSelectorDisplayType value={displayType}
                                       onChange={value => setDisplayType(value || AccommodationType.RENTAL)} />
      </SearchBarItem>

      <button type="button"
              className={classNames("search-bar-item", "no-border", { active: displayType === AccommodationType.SALE })}
              onClick={() => setDisplayType(AccommodationType.SALE)}>
        {intl.formatMessage(genericTermTranslations.sale)}
      </button>

      {filtersRendering}

      <button type="submit" className="btn-red">
        {intl.formatMessage(globalTranslations.search)}
        <ArrowRightSvg />
      </button>

    </form>);

}

export default SearchBar;
